/**
 * <s-scroller> — progressive enhancement for a native scroll-snap track.
 * Markup: a [data-scroller-track] element that scrolls horizontally, plus optional
 * [data-scroller-prev] / [data-scroller-next] buttons. Without JS the track still swipes and scrolls.
 * Buttons scroll by one tile (the first child's width plus the gap) and disable at each end.
 * Optional [data-scroller-dot] buttons, one per tile: clicking jumps to that tile; the tile in view gets aria-current="true".
 * Optional `loop` attribute: swiping past, or stepping past, either end wraps to the other end, and the buttons never disable.
 */
class SScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-scroller-track]');
    this.prev = this.querySelector('[data-scroller-prev]');
    this.next = this.querySelector('[data-scroller-next]');
    if (!this.track) return;
    this.loop = this.hasAttribute('loop');
    this.onScroll = () => this.update();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.dots = [...this.querySelectorAll('[data-scroller-dot]')];
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));

    if (this.loop) {
      this.onTouchStart = (event) => {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
        this.touchAtStart = this.atStart();
        this.touchAtEnd = this.atEnd();
      };
      this.onTouchEnd = (event) => {
        if (this.touchStartX == null) return;
        const dx = event.changedTouches[0].clientX - this.touchStartX;
        const dy = event.changedTouches[0].clientY - this.touchStartY;
        this.touchStartX = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        const forward = this.isRtl() ? dx > 0 : dx < 0;
        if (forward && this.touchAtEnd) this.goTo(0);
        else if (!forward && this.touchAtStart) this.goTo(this.count() - 1);
      };
      this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
    }
    this.update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    if (this.onTouchStart) this.track?.removeEventListener('touchstart', this.onTouchStart);
    if (this.onTouchEnd) this.track?.removeEventListener('touchend', this.onTouchEnd);
  }

  isRtl() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  behavior() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  count() {
    return this.track.children.length;
  }

  position() {
    return Math.abs(this.track.scrollLeft);
  }

  atStart() {
    return this.position() <= 1;
  }

  atEnd() {
    return this.position() >= this.track.scrollWidth - this.track.clientWidth - 1;
  }

  tileDistance() {
    const tile = this.track.firstElementChild;
    if (!tile) return 0;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return tile.getBoundingClientRect().width + gap;
  }

  goTo(index) {
    const left = this.tileDistance() * index * (this.isRtl() ? -1 : 1);
    this.track.scrollTo({ left, behavior: this.behavior() });
  }

  step(direction) {
    if (!this.track.firstElementChild) return;
    if (this.loop && direction > 0 && this.atEnd()) return this.goTo(0);
    if (this.loop && direction < 0 && this.atStart()) return this.goTo(this.count() - 1);
    const distance = this.tileDistance() * direction * (this.isRtl() ? -1 : 1);
    this.track.scrollBy({ left: distance, behavior: this.behavior() });
  }

  update() {
    if (!this.loop) {
      if (this.prev) this.prev.disabled = this.atStart();
      if (this.next) this.next.disabled = this.atEnd();
    }
    if (this.dots?.length) {
      const distance = this.tileDistance();
      const active = distance ? Math.min(this.dots.length - 1, Math.round(this.position() / distance)) : 0;
      this.dots.forEach((dot, index) => {
        if (index === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  }
}

if (!customElements.get('s-scroller')) customElements.define('s-scroller', SScroller);
